import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Shield, X } from 'lucide-react';

const COOKIE_KEY = 'laizyones_cookie_consent';

const CookieBanner = () => {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem(COOKIE_KEY);
    if (!consent) {
      const timer = setTimeout(() => setVisible(true), 1200);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    localStorage.setItem(COOKIE_KEY, JSON.stringify({ essential: true, analytics: true, marketing: true, timestamp: Date.now() }));
    setVisible(false);
  };

  const handleAcceptEssential = () => {
    localStorage.setItem(COOKIE_KEY, JSON.stringify({ essential: true, analytics: false, marketing: false, timestamp: Date.now() }));
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="cookie-banner"
      className="fixed bottom-0 left-0 right-0 z-[9999] p-4 md:p-6"
      style={{ animation: 'slideUp 0.4s ease-out' }}
    >
      <div className="max-w-4xl mx-auto rounded-2xl border border-white/10 shadow-2xl"
        style={{ background: 'rgba(5,14,28,0.97)', backdropFilter: 'blur(20px)' }}>
        
        <div className="p-5 md:p-6">
          <div className="flex items-start justify-between gap-4 mb-3">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center flex-shrink-0">
                <Shield className="w-4 h-4 text-emerald-400" />
              </div>
              <h3 className="font-['Space_Grotesk'] font-semibold text-white text-base">
                Datenschutz-Einstellungen
              </h3>
            </div>
            <button
              onClick={handleAcceptEssential}
              className="text-gray-500 hover:text-white transition-colors"
              aria-label="Schließen"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <p className="text-gray-400 text-sm leading-relaxed mb-4">
            Wir verwenden Cookies, um Ihnen die bestmögliche Erfahrung auf unserer Website zu bieten.
            Technisch notwendige Cookies sind für die Grundfunktionen erforderlich.
            Weitere Informationen finden Sie in unserer{' '}
            <Link to="/datenschutz" className="text-emerald-400 hover:text-emerald-300 underline underline-offset-2">
              Datenschutzerklärung
            </Link>.
          </p>

          {showDetails && (
            <div className="mb-4 space-y-2 text-sm">
              <div className="flex items-center justify-between p-3 rounded-lg bg-white/5 border border-white/5">
                <div>
                  <span className="text-white font-medium">Technisch notwendig</span>
                  <p className="text-gray-500 text-xs mt-0.5">Erforderlich für Grundfunktionen der Website</p>
                </div>
                <span className="text-emerald-400 text-xs font-medium">Immer aktiv</span>
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg bg-white/5 border border-white/5">
                <div>
                  <span className="text-white font-medium">Analyse</span>
                  <p className="text-gray-500 text-xs mt-0.5">Hilft uns, die Nutzung der Website zu verstehen</p>
                </div>
                <span className="text-gray-500 text-xs">Optional</span>
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg bg-white/5 border border-white/5">
                <div>
                  <span className="text-white font-medium">Marketing</span>
                  <p className="text-gray-500 text-xs mt-0.5">Ermöglicht personalisierte Inhalte</p>
                </div>
                <span className="text-gray-500 text-xs">Optional</span>
              </div>
            </div>
          )}

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <button
              data-testid="cookie-accept-all"
              onClick={handleAcceptAll}
              className="px-5 py-2.5 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-white text-sm font-semibold transition-colors"
            >
              Alle akzeptieren
            </button>
            <button
              data-testid="cookie-accept-essential"
              onClick={handleAcceptEssential}
              className="px-5 py-2.5 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 text-gray-300 text-sm font-medium transition-colors"
            >
              Nur notwendige
            </button>
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="px-5 py-2.5 text-gray-500 hover:text-gray-300 text-sm transition-colors"
            >
              {showDetails ? 'Weniger anzeigen' : 'Details anzeigen'}
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes slideUp {
          from { transform: translateY(100%); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default CookieBanner;
